// ─── Modal / Sheet primitive (#2067) ────────────────────────────────────────
//
// The 2026-08-03 audit found the overlay + `role="dialog"` + Escape handler
// combination rebuilt eleven times across nine CSS prefixes, with the
// accessibility half already diverged: two dialogs had `role` but no
// `aria-modal`, four had neither, Escape was wired twelve separate times (half
// on `document`, half on `window`) and NOTHING had a focus trap.
//
// This module owns the BEHAVIOUR — overlay, backdrop dismissal, Escape,
// `role`/`aria-modal`/accessible name, focus trap, focus restoration — once.
// It deliberately does NOT own the LOOK: each surface keeps its own class
// prefix, registered in `DIALOG_VARIANTS` below, so this refactor moves no
// pixels. See the PR body for the declaration-by-declaration diff of the nine
// overlays: they share `position:fixed;inset:0` and nothing else — three
// different scrim alphas, five z-index tiers and four layout modes — so a
// single visual overlay class would have restyled real surfaces. Collapsing
// the parts that ARE identical is done in `styles.css` by selector grouping,
// which changes no computed value and no DOM class.
//
// ── Layouts ─────────────────────────────────────────────────────────────────
// `nested`   backdrop wraps the panel. Centred dialogs and the wizard invite
//            sheet (a flex-end child of its backdrop).
// `sibling`  backdrop and panel are siblings. The bottom sheets are
//            `position:fixed` with their own z-index tier; nesting them inside
//            the backdrop would make them inherit its `cursor:pointer` and
//            would drop the pair into the backdrop's stacking context.
// `bare`     the backdrop IS the dialog. The scorecard lightbox has no panel —
//            the image and the fixed close button are direct children.
//
// ── Focus ───────────────────────────────────────────────────────────────────
// The element to restore focus to is captured during RENDER, not in an effect:
// child effects (and React's `autoFocus`) run before the parent's, so by the
// time this component's effect fires the focus has often already moved into
// the panel, and an effect-time capture would "restore" focus to a node that
// is about to be unmounted.
import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "[tabindex]",
].join(",");

/**
 * Focusable descendants of `root`, in DOM order.
 *
 * Visibility is deliberately NOT considered: jsdom reports every element as
 * unrendered (`offsetParent === null`, zero-size rects), so a visibility filter
 * would make the trap a no-op under test — exactly where it is asserted.
 */
export function focusableWithin(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) =>
      !el.hasAttribute("disabled") &&
      el.getAttribute("aria-hidden") !== "true" &&
      el.getAttribute("tabindex") !== "-1",
  );
}

/**
 * Dialog behaviour for a panel element: Escape, Tab trapping, initial focus
 * and focus restoration. Exported for a surface that needs its own markup.
 *
 * @param panelRef  ref to the element that holds the dialog's focusables
 */
export function useDialogBehaviour(
  panelRef,
  { onClose, closeOnEscape = true, trapFocus = true, restoreFocus = true } = {},
) {
  // Captured on the first render — see the focus note in the file header.
  const previousFocus = useRef(undefined);
  if (previousFocus.current === undefined) {
    previousFocus.current =
      typeof document === "undefined" ? null : document.activeElement;
  }

  // Latest onClose without re-binding the key listener on every render.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  // Initial focus in, focus out on unmount. Mount-only by design.
  useEffect(() => {
    if (!trapFocus) return undefined;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      const first = focusableWithin(panel)[0];
      if (first) {
        first.focus();
      } else {
        panel.setAttribute("tabindex", "-1");
        panel.focus();
      }
    }
    const previous = previousFocus.current;
    return () => {
      if (
        restoreFocus &&
        previous &&
        typeof previous.focus === "function" &&
        document.contains(previous)
      ) {
        previous.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!closeOnEscape && !trapFocus) return undefined;
    function onKeyDown(e) {
      if (closeOnEscape && e.key === "Escape") {
        closeRef.current?.();
        return;
      }
      if (!trapFocus || e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = focusableWithin(panel);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!panel.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeOnEscape, trapFocus]);
}

/**
 * The nine dialog surfaces, one entry each. A variant is a NAME for an existing
 * look, not a new one — `overlay`/`panel` are the classes those call sites
 * already carried, so adding a surface here is a rename, never a restyle.
 */
export const DIALOG_VARIANTS = {
  // ── Centred dialogs ──
  feedback: { layout: "nested", overlay: "feedback-overlay", panel: "feedback-modal panel" },
  faceZoom: { layout: "nested", overlay: "feedback-overlay", panel: "panel heatmap-zoom-modal" },
  palette: { layout: "nested", overlay: "palette-backdrop", panel: "palette" },
  numberPrompt: { layout: "nested", overlay: "ctr-numprompt-overlay", panel: "ctr-numprompt" },
  // ── Full-bleed ──
  lightbox: { layout: "bare", overlay: "round-scorecard-overlay" },
  // ── Bottom sheets ──
  // `wizardInvite` is the one sheet positioned by its backdrop (flex-end)
  // rather than by `position:fixed`, so it nests.
  wizardInvite: { layout: "nested", overlay: "wi-sheet-backdrop", panel: "wi-sheet" },
  arrowLog: { layout: "sibling", overlay: "als-overlay", panel: "als-sheet" },
  // The counter's save sheet deliberately reuses the ArrowLogSheet chrome
  // (#1813). Naming it here makes that an explicit registry entry instead of
  // an unrelated component's class strings copied into pages/Counter.jsx.
  counterSave: { layout: "sibling", overlay: "als-overlay", panel: "als-sheet" },
  roundSettings: { layout: "sibling", overlay: "rs-sheet-overlay", panel: "rs-sheet" },
  baleQr: { layout: "sibling", overlay: "bale-qr-overlay", panel: "bale-qr-sheet" },
  baleScorecard: { layout: "sibling", overlay: "bale-sc-overlay", panel: "bale-sc-sheet" },
  // Not in the audit's list — found by the ratchet at the bottom of
  // Modal.test.jsx. BaleLaneEditor is a TWELFTH rebuild, on BaleScorecard's
  // chrome, and had neither `role`, `aria-modal` nor Escape.
  baleLanes: { layout: "sibling", overlay: "bale-sc-overlay", panel: "bale-sc-sheet" },
  // LibrarySheet's "Browse the library" catalog sheet (#2375, M13-W3) — a
  // THIRTEENTH rebuild, caught by the same ratchet. Reuses `roundSettings`'s
  // overlay class (no new scrim needed) on its own `lib-sheet` panel chrome
  // (search box + tree body + a >=768px popover conversion `roundSettings`
  // doesn't have).
  library: { layout: "sibling", overlay: "rs-sheet-overlay", panel: "lib-sheet" },
  // RBTE stats entity card — a leaderboard row's detail (club / state /
  // archer …) opens as a centred card over the board instead of an inline
  // panel that shoves the table down. Reuses the feedback scrim; the panel
  // chrome lives in pages/rbte/rbte.css.
  rbteEntity: { layout: "nested", overlay: "feedback-overlay", panel: "panel rbte-entity-modal" },
};

/**
 * Modal — one overlay + dialog panel with the a11y contract attached.
 *
 * Props:
 *   variant           key into DIALOG_VARIANTS; picks the layout and classes
 *   onClose           () => void — Escape, backdrop click
 *   label             accessible name (`aria-label`)
 *   labelledBy        id of an element naming the dialog (`aria-labelledby`)
 *   panelClassName    extra classes on the panel
 *   closeOnBackdrop   default true
 *   closeOnEscape     default true
 *   trapFocus         default true
 *   restoreFocus      default true — return focus to the trigger on unmount
 *
 * Mount only while open: every hook here is scoped to the open lifetime, so
 * callers keep their existing `if (!open) return null` guard.
 */
export function Modal({
  variant,
  onClose,
  label,
  labelledBy,
  panelClassName,
  closeOnBackdrop = true,
  closeOnEscape = true,
  trapFocus = true,
  restoreFocus = true,
  children,
}) {
  // Hooks first — a bad variant must not change the hook order (React #310).
  const panelRef = useRef(null);
  useDialogBehaviour(panelRef, { onClose, closeOnEscape, trapFocus, restoreFocus });

  const spec = DIALOG_VARIANTS[variant];
  if (!spec) throw new Error(`Modal: unknown variant "${variant}"`);

  // Only a click that LANDS on the backdrop dismisses. `target === currentTarget`
  // does the same job as the `stopPropagation()` half of the old call sites
  // without swallowing the event for anything else listening above.
  function onBackdropClick(e) {
    if (!closeOnBackdrop) return;
    if (e.target === e.currentTarget) onClose?.();
  }

  const a11y = {
    role: "dialog",
    "aria-modal": "true",
    ...(labelledBy ? { "aria-labelledby": labelledBy } : {}),
    ...(label ? { "aria-label": label } : {}),
  };

  const panelClass = [spec.panel, panelClassName].filter(Boolean).join(" ");

  if (spec.layout === "bare") {
    return (
      <div
        ref={panelRef}
        className={[spec.overlay, panelClassName].filter(Boolean).join(" ")}
        onClick={onBackdropClick}
        {...a11y}
      >
        {children}
      </div>
    );
  }

  if (spec.layout === "sibling") {
    return (
      <>
        <div
          className={spec.overlay}
          onClick={closeOnBackdrop ? () => onClose?.() : undefined}
        />
        <div ref={panelRef} className={panelClass} {...a11y}>
          {children}
        </div>
      </>
    );
  }

  return (
    <div className={spec.overlay} onClick={onBackdropClick}>
      <div ref={panelRef} className={panelClass} {...a11y}>
        {children}
      </div>
    </div>
  );
}
